/**
 * Rótulos e selos dos atendimentos (visual v2): tipo, status e prioridade
 * com as cores do tema, e o nome de exibição do contato.
 */
import { AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/utils/cn'
import type { Client, ClientInteraction } from '@/types/client'

export type InteractionType = ClientInteraction['type']
export type InteractionStatus = ClientInteraction['status']
export type InteractionPriority = ClientInteraction['priority']

export const interactionTypeLabels: Record<InteractionType, string> = {
  meeting: 'Reunião',
  phone_call: 'Ligação',
  email: 'E-mail',
  whatsapp: 'WhatsApp',
  consultation: 'Consulta',
  document_review: 'Revisão de Documento',
  follow_up: 'Acompanhamento',
  initial_contact: 'Contato Inicial',
  court_appearance: 'Audiência',
  negotiation: 'Negociação',
  other: 'Outro'
}

export const interactionStatusLabels: Record<InteractionStatus, string> = {
  scheduled: 'Agendado',
  completed: 'Concluído',
  in_progress: 'Em Andamento',
  cancelled: 'Cancelado',
  converted_to_case: 'Convertido em Caso',
  converted_to_legal_process: 'Convertido em Processo Judicial'
}

export const interactionPriorityLabels: Record<InteractionPriority, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente'
}

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive'

const STATUS_VARIANT: Record<InteractionStatus, BadgeVariant> = {
  scheduled: 'outline',
  completed: 'secondary',
  in_progress: 'default',
  cancelled: 'destructive',
  converted_to_case: 'secondary',
  converted_to_legal_process: 'secondary'
}

export function InteractionStatusBadge({ status, className }: { status: InteractionStatus; className?: string }) {
  return (
    <Badge variant={STATUS_VARIANT[status] ?? 'secondary'} className={className}>
      {interactionStatusLabels[status] ?? status}
    </Badge>
  )
}

export function InteractionPriorityText({ priority, className }: { priority: InteractionPriority; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium',
        priority === 'urgent' ? 'text-destructive' : priority === 'high' ? 'text-foreground' : 'text-muted-foreground',
        className,
      )}
    >
      {priority === 'urgent' && <AlertTriangle className="size-3" aria-hidden="true" />}
      {interactionPriorityLabels[priority] ?? priority}
    </span>
  )
}

/** Nome do contato: pessoa física pelo nome completo; empresa pelo nome fantasia ou razão social. */
export function getClientDisplayName(client: Client | null | undefined, fallback = 'Desconhecido'): string {
  if (!client) return fallback
  if (client.clientType === 'PERSON' && client.personalData) {
    return client.personalData.fullName || 'Sem nome'
  }
  if (client.clientType === 'COMPANY' && client.companyData) {
    return client.companyData.tradingName || client.companyData.companyName || 'Sem nome'
  }
  return 'Sem nome'
}
